import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImagePlus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePrivateImageGeneration } from '@/hooks/private-image-generator/usePrivateImageGeneration';

interface PrivateImageFormProps {
  onImageCreated?: () => void;
}

export const PrivateImageForm: React.FC<PrivateImageFormProps> = ({
  onImageCreated
}) => {
  const [imageDescription, setImageDescription] = useState('');

  // Use the existing private image generation hook
  const {
    isGenerating,
    handleGenerateImage
  } = usePrivateImageGeneration({
    imageDescription,
    width: '1024',
    height: '1024',
    selectedType: 'type-photo',
    selectedStyle: null,
    selectedInfluence: null,
    selectedBackground: null,
    styleOptions: [],
    influenceOptions: [],
    colorOptions: [],
    backgroundOptions: [],
    environmentOptions: [],
    moodOptions: []
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!imageDescription.trim()) {
      return;
    }

    try {
      await handleGenerateImage();
      // Clear form and notify parent
      setImageDescription('');
      onImageCreated?.();
    } catch (error) {
      console.error('[PrivateImageForm] Error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form Header */}
      <div className="text-center space-y-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Describe what you want to create and AI will generate it for you
        </p>
      </div>

      {/* Image Description Input */}
      <div className="space-y-2">
        <Label htmlFor="imageDescription" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Image Description
        </Label>
        <Input
          id="imageDescription"
          type="text"
          placeholder="e.g., A serene mountain landscape at sunset with clouds"
          value={imageDescription}
          onChange={(e) => setImageDescription(e.target.value)}
          disabled={isGenerating}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Generate Button */}
      <motion.div
        whileHover={{ scale: isGenerating ? 1 : 1.02 }}
        whileTap={{ scale: isGenerating ? 1 : 0.98 }}
      >
        <Button
          type="submit"
          disabled={isGenerating || !imageDescription.trim()}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-center gap-3">
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Generating Image...</span>
              </>
            ) : (
              <>
                <ImagePlus className="w-5 h-5" />
                <span>Generate Image</span>
              </>
            )}
          </div>
        </Button>
      </motion.div>
    </form>
  );
}; 